import { action, computed, makeObservable, reaction } from 'mobx';

import { AlertsStore } from '@/configs/store/AlertsStore';
import { LoadingStageModel } from '@/models/LoadingStageModel';
import { LocalStore } from '@/models/LocalStore';
import { ValueModel } from '@/models/ValueModel';
import { trackerApi } from '@/tracker/api/trackerApi';
import { matchesQuery } from '@/tracker/entities/issue';
import { TaskFiltersModel } from '@/tracker/features/taskFilters';
import { SEARCH_PAGE_SIZE } from '@/tracker/hooks/useServerSearch';
import { StoredValueModel, TrackerStore } from '@/tracker/model';
import {
  BoardColumn,
  CreateSprintRequest,
  Queue,
  QueueRef,
  Sprint,
  Task,
  TaskBasic,
  TaskFilters,
  TaskRef,
  UserRef,
} from '@/tracker/types';
import { showError } from '@/tracker/utils/showError';

import { BoardLayoutStore } from './BoardLayoutStore';
import { mostCommonQueue } from './mostCommonQueue';

/** Быстрый фильтр по спринту: id спринта или все задачи доски. Задачи без спринта — на вкладке «Бэклог» */
export type SprintFilter = string;
export const ALL_TASKS = 'all';

export type BoardColumnWithTasks = BoardColumn & { tasks: TaskBasic[] };

/** Свимлейны — строки доски, как в Яндекс Трекере: по исполнителю или по родительской задаче */
export type Swimlanes = 'none' | 'assignee' | 'parent';

/** Чья строка. null — «Без исполнителя» / «Без родителя» */
export type LaneOwner =
  | { type: 'assignee'; user: UserRef | null }
  | { type: 'parent'; parent: TaskRef | null };

export type BoardLane = {
  key: string;
  owner: LaneOwner;
  columns: BoardColumnWithTasks[];
  total: number;
};

const NO_OWNER = 'none';

const SWIMLANES: Swimlanes[] = ['none', 'assignee', 'parent'];

const isSwimlanes = (saved: unknown): saved is Swimlanes => SWIMLANES.includes(saved as Swimlanes);

const isQueueRef = (saved: unknown): saved is QueueRef | null =>
  saved === null ||
  (typeof (saved as QueueRef).key === 'string' && typeof (saved as QueueRef).name === 'string');

const laneOwnerOf = ({ task, swimlanes }: { task: TaskBasic; swimlanes: Swimlanes }): LaneOwner =>
  swimlanes === 'assignee'
    ? { type: 'assignee', user: task.assignee }
    : { type: 'parent', parent: task.parent };

const laneKeyOf = (owner: LaneOwner) => {
  if (owner.type === 'assignee') return owner.user?.id ?? NO_OWNER;

  return owner.parent?.key ?? NO_OWNER;
};

/** Исполнители — по имени, родители — как встретились на доске слева направо; строка «без» — последней */
const compareLanes = (a: BoardLane, b: BoardLane) => {
  if (a.key === NO_OWNER || b.key === NO_OWNER)
    return Number(a.key === NO_OWNER) - Number(b.key === NO_OWNER);

  if (a.owner.type === 'assignee' && b.owner.type === 'assignee') {
    return (a.owner.user?.displayName ?? '').localeCompare(b.owner.user?.displayName ?? '', 'ru');
  }

  return 0;
};

export class BoardStore extends LocalStore {
  private readonly _alertsStore: AlertsStore;
  private readonly _trackerStore: TrackerStore;
  private readonly _layout: BoardLayoutStore;
  private readonly _boardId: string;
  private readonly _sprints = new ValueModel<Sprint[]>([]);
  private readonly _tasks = new ValueModel<TaskBasic[]>([]);
  private readonly _sprintFilter = new ValueModel<SprintFilter>(ALL_TASKS);
  /** Плашки фильтров над доской; спринт выбирается отдельно — у него своё умолчание */
  readonly filters = new TaskFiltersModel();
  /** Настройка одного браузера: в контракте доски свимлейнов нет */
  private readonly _swimlanes: StoredValueModel<Swimlanes>;
  /** Очередь, в которой последний раз создавали задачу из колонки этой доски */
  private readonly _quickCreateQueue: StoredValueModel<QueueRef | null>;
  private readonly _loadingStage = new LoadingStageModel();
  private readonly _contentStage = new LoadingStageModel();
  private readonly _savingStage = new LoadingStageModel();
  /** Ответ на устаревшие фильтры не должен перетереть свежий */
  private _contentRequest = 0;

  constructor({
    alertsStore,
    trackerStore,
    layout,
  }: {
    alertsStore: AlertsStore;
    trackerStore: TrackerStore;
    /** Доска уже загружена: вкладка показывается под шапкой после неё */
    layout: BoardLayoutStore;
  }) {
    super();
    this._alertsStore = alertsStore;
    this._trackerStore = trackerStore;
    this._layout = layout;
    this._boardId = layout.boardId;
    this._swimlanes = new StoredValueModel({
      key: `tracker.board.swimlanes.${layout.boardId}`,
      fallback: 'none',
      isValid: isSwimlanes,
    });
    this._quickCreateQueue = new StoredValueModel({
      key: `tracker.board.quickCreateQueue.${layout.boardId}`,
      fallback: null,
      isValid: isQueueRef,
    });

    makeObservable(this, {
      sprints: computed,
      columns: computed,
      swimlanes: computed,
      lanes: computed,
      sprintFilter: computed,
      isError: computed,
      isContentLoading: computed,
      isSaving: computed,
      quickCreateQueue: computed,
      load: action.bound,
      createSprint: action.bound,
      createInColumn: action.bound,
      addToColumn: action.bound,
      searchQueues: action.bound,
      searchTasks: action.bound,
      setSprintFilter: action.bound,
      setSwimlanes: action.bound,
    });
  }

  /** Незавершённые спринты доски: активный и черновики */
  get sprints() {
    return this._sprints.value;
  }

  /** Колонки с задачами: задача стоит в колонке своего статуса, без подходящей колонки — не видна */
  get columns(): BoardColumnWithTasks[] {
    const query = this._layout.query;
    // Доска грузит все свои задачи — ищем на месте, без запросов: сразу по мере ввода
    const tasks = this._tasks.value.filter((task) => matchesQuery({ task, query }));

    return (this._layout.board?.columns ?? []).map((column) => ({
      ...column,
      tasks: tasks.filter((task) =>
        column.statuses.some((status) => status.key === task.status.key),
      ),
    }));
  }

  get swimlanes() {
    return this._swimlanes.value;
  }

  /** Строки доски с теми же колонками. null — свимлейны выключены. Пустых строк нет */
  get lanes(): BoardLane[] | null {
    const swimlanes = this._swimlanes.value;

    if (swimlanes === 'none') return null;

    const columns = this.columns;
    const lanes = new Map<string, BoardLane>();

    // Задачи вне колонок на доске не видны — и строк для них не заводим
    columns.forEach((column, index) =>
      column.tasks.forEach((task) => {
        const owner = laneOwnerOf({ task, swimlanes });
        const key = laneKeyOf(owner);
        const lane = lanes.get(key) ?? {
          key,
          owner,
          columns: columns.map((item) => ({ ...item, tasks: [] })),
          total: 0,
        };

        lane.columns[index].tasks.push(task);
        lane.total += 1;
        lanes.set(key, lane);
      }),
    );

    return [...lanes.values()].sort(compareLanes);
  }

  get sprintFilter() {
    return this._sprintFilter.value;
  }

  get isError() {
    return this._loadingStage.isError;
  }

  get isContentLoading() {
    return this._contentStage.isLoading;
  }

  get isSaving() {
    return this._savingStage.isLoading;
  }

  /**
   * Очередь для быстрого создания: последняя на этой доске, иначе самая частая среди её задач.
   * К очереди доска не привязана — так угадываем, как Яндекс по правилу автодобавления
   */
  get quickCreateQueue() {
    return this._quickCreateQueue.value ?? mostCommonQueue(this._tasks.value);
  }

  setSprintFilter(value: SprintFilter) {
    this._sprintFilter.change(value);
  }

  setSwimlanes(value: Swimlanes) {
    this._swimlanes.change(value);
  }

  async load() {
    try {
      this._loadingStage.loading();
      await this.loadSprints();
      // Как в Яндекс Трекере: по умолчанию доска показывает текущий спринт
      this._sprintFilter.change(this._layout.board?.activeSprint?.id ?? ALL_TASKS);
      this._loadingStage.success();
    } catch (error) {
      this._loadingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось загрузить спринты', error });

      return;
    }

    this.addReactions([
      reaction(
        () => [this._sprintFilter.value, this.filters.values],
        () => this.loadTasks(),
        { fireImmediately: true },
      ),
      reaction(
        () => this._trackerStore.updatedIssue,
        (updated) => updated && this.applyUpdatedIssue(updated),
      ),
    ]);
  }

  /** Спринт из выбора спринта над доской, как в Яндекс Трекере. Фильтр не меняем. true — создан */
  async createSprint(data: CreateSprintRequest) {
    try {
      this._savingStage.loading();
      await trackerApi.createSprint({ boardId: this._boardId, data });
      await this.loadSprints();
      this._savingStage.success();

      return true;
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось создать спринт', error });

      return false;
    }
  }

  /**
   * Новая задача из «+» колонки, как в Яндекс Трекере: в статусе колонки (первом, который есть
   * у очереди), на этой доске и в выбранном спринте. true — создана
   */
  async createInColumn({
    queueKey,
    summary,
    statuses,
  }: {
    queueKey: string;
    summary: string;
    statuses: string[];
  }) {
    let task: Task;

    try {
      this._savingStage.loading();

      const queue = await trackerApi.getQueue(queueKey);
      const status = statuses.find((key) => queue.statuses.some((item) => item.key === key));

      task = await trackerApi.createTask({ queue: queueKey, summary, ...(status && { status }) });
      this._quickCreateQueue.change({ key: queue.key, name: queue.name });
    } catch (error) {
      this._savingStage.error();
      showError({ alertsStore: this._alertsStore, title: 'Не удалось создать задачу', error });

      return false;
    }

    await this.placeOnBoard(task.key);

    return true;
  }

  /**
   * Существующая задача из «+» колонки: на эту доску и в выбранный спринт. Статус не из этой
   * колонки меняем на статус колонки — иначе задача встала бы на доску, но в колонке её не было бы
   */
  async addToColumn({ task, statuses }: { task: TaskBasic; statuses: string[] }) {
    this._savingStage.loading();

    if (!statuses.includes(task.status.key)) await this.moveToColumn({ task, statuses });

    await this.placeOnBoard(task.key);

    return this._savingStage.isSuccess;
  }

  /** Очереди для быстрого создания — подсказки сервера: начало ключа, мои первыми */
  async searchQueues(query: string): Promise<Queue[]> {
    try {
      return await trackerApi.suggestQueues({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });
    } catch {
      return [];
    }
  }

  /** Задачи для «Существующая» — подсказки сервера; без ввода — мои недавние задачи */
  async searchTasks(query: string): Promise<TaskBasic[]> {
    try {
      return await trackerApi.suggestTasks({
        query: query.trim() || undefined,
        pageSize: SEARCH_PAGE_SIZE,
      });
    } catch {
      return [];
    }
  }

  /** Первый статус колонки, который есть у очереди задачи. Не вышло — сообщаем и ставим как есть */
  private async moveToColumn({ task, statuses }: { task: TaskBasic; statuses: string[] }) {
    try {
      const queue = await trackerApi.getQueue(task.queue.key);
      const status = statuses.find((key) => queue.statuses.some((item) => item.key === key));

      if (!status) {
        showError({
          alertsStore: this._alertsStore,
          title: `${task.key} не будет видна в колонке`,
          error: 'У очереди задачи нет статусов этой колонки',
        });

        return;
      }

      await trackerApi.updateTask({ key: task.key, data: { status } });
    } catch (error) {
      showError({
        alertsStore: this._alertsStore,
        title: `${task.key}: не удалось сменить статус`,
        error,
      });
    }
  }

  /**
   * В POST /tasks нет ни доски, ни спринта: ставим отдельными запросами. Задача уже есть —
   * при ошибке сообщаем, но доску всё равно перечитываем
   */
  private async placeOnBoard(key: string) {
    const sprint = this._sprintFilter.value;

    try {
      await trackerApi.addTasksToBoard({ boardId: this._boardId, keys: [key] });

      if (sprint !== ALL_TASKS) {
        await trackerApi.addTasksToSprint({
          boardId: this._boardId,
          sprintId: sprint,
          keys: [key],
        });
      }

      this._savingStage.success();
    } catch (error) {
      this._savingStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: `${key}: не удалось добавить на доску`,
        error,
      });
    }

    this.loadTasks();
  }

  private async loadSprints() {
    this._sprints.change(
      await trackerApi.searchAllSprints({
        boardId: this._boardId,
        request: { filters: { status: { in: ['active', 'draft'] } } },
      }),
    );
  }

  /**
   * Задачу поменяли в карточке. Колонка считается по статусу — строку меняем на месте;
   * перезагрузка, только если задачу добавили на эту доску или в выбранный спринт
   */
  private applyUpdatedIssue(updated: Task) {
    const sprint = this._sprintFilter.value;
    const belongs =
      sprint === ALL_TASKS
        ? updated.boards.some((board) => board.id === this._boardId)
        : updated.sprints.some((item) => item.id === sprint);
    const isShown = this._tasks.value.some((task) => task.key === updated.key);

    if (isShown) {
      this._tasks.change(
        belongs
          ? this._tasks.value.map((task) => (task.key === updated.key ? updated : task))
          : this._tasks.value.filter((task) => task.key !== updated.key),
      );

      return;
    }

    if (belongs) this.loadTasks();
  }

  private taskFilters(): TaskFilters {
    const sprint = this._sprintFilter.value;
    const filters = this.filters.toTaskFilters();

    // В спринт задачу кладут и без доски (создание задачи, карточка) — а спринт и так этой доски
    if (sprint !== ALL_TASKS) return { ...filters, sprint: { eq: sprint } };

    return { ...filters, board: { eq: this._boardId } };
  }

  private async loadTasks() {
    const request = ++this._contentRequest;

    try {
      this._contentStage.loading();

      const tasks = await trackerApi.searchAllTasks({ filters: this.taskFilters() });

      if (request !== this._contentRequest) return;

      this._tasks.change(tasks);
      this._contentStage.success();
    } catch (error) {
      if (request !== this._contentRequest) return;

      this._contentStage.error();
      showError({
        alertsStore: this._alertsStore,
        title: 'Не удалось загрузить задачи доски',
        error,
      });
    }
  }
}
